import React, { useState, useEffect } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Alert, IconButton, Stack, Tabs, Tab,
} from '@mui/material';
import BackspaceIcon from '@mui/icons-material/Backspace';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { useTechnicianAuth } from '../../contexts/TechnicianAuthContext';
import { managerLogin } from '../../services/api';
import { isIos, isStandalone } from '../../services/push';

const PIN_LENGTH = 6;

/**
 * Acceso a la app movil. Los tecnicos entran con codigo + PIN y los gestores con
 * su email y contrasena de siempre; se entra una vez y la sesion se mantiene
 * (ver renewSession en api.js). Pensado para usarse en obra desde el movil: teclado
 * numerico grande y el codigo precargado cuando se llega desde el QR
 * (/tecnicos?c=CODIGO).
 */
export default function TechnicianLogin() {
  const { login } = useTechnicianAuth();
  const [loginCode, setLoginCode] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('app_login_mode') === 'manager' ? 'manager' : 'technician'; } catch (e) { return 'technician'; }
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // El QR trae el codigo en la query: el tecnico solo teclea el PIN
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('c');
    if (code) {
      setLoginCode(code.toUpperCase());
      setMode('technician');
    }
  }, []);

  const changeMode = (_, value) => {
    setMode(value);
    setError('');
    try { localStorage.setItem('app_login_mode', value); } catch (e) { /* sin almacenamiento */ }
  };

  // El gestor es el mismo usuario del escritorio: tras entrar va a los chats, y
  // desde el menu tiene el gestor completo. Se recarga para que arranque con su sesion.
  const submitManager = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await managerLogin(email.trim(), password);
      window.location.replace('/chats');
    } catch (err) {
      setError(err.response?.data?.detail || 'Email o contrasena incorrectos');
      setLoading(false);
    }
  };

  const submit = async (pinValue) => {
    setError('');
    setLoading(true);
    try {
      await login(loginCode.trim().toUpperCase(), pinValue);
    } catch (err) {
      setError(err.response?.data?.detail || 'Codigo o PIN incorrectos');
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  const pressDigit = (digit) => {
    if (pin.length >= PIN_LENGTH) return;
    const next = pin + digit;
    setPin(next);
    // Al completar el PIN entra solo: un digito menos que pulsar
    if (next.length === PIN_LENGTH && loginCode.trim()) submit(next);
  };

  const keypad = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', backgroundColor: '#ffebee', p: 2,
    }}>
      <Paper sx={{ p: 3, maxWidth: 380, width: '100%' }} elevation={3}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <EngineeringIcon color="primary" sx={{ fontSize: 48 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">CNPK GPI</Typography>
          <Typography variant="body2" color="textSecondary">Chats y tareas de obra</Typography>
        </Box>

        {/* En iPhone la app instalada guarda su propia sesion, separada de
            Safari: si se entra primero en Safari y se instala despues, hay que
            volver a entrar. Se avisa antes. */}
        {isIos() && !isStandalone() && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Antes de entrar, instala la app: pulsa <strong>Compartir</strong> y{' '}
            <strong>Anadir a pantalla de inicio</strong>, y abrela desde ese icono.
          </Alert>
        )}

        <Tabs value={mode} onChange={changeMode} variant="fullWidth" sx={{ mb: 2 }}>
          <Tab label="Tecnico" value="technician" />
          <Tab label="Gestor" value="manager" />
        </Tabs>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {mode === 'manager' && (
          <form onSubmit={submitManager}>
            <TextField fullWidth label="Email" type="email" value={email} required autoFocus
              autoComplete="username" onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth label="Contrasena" type="password" value={password} required
              autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} sx={{ mb: 2 }} />
            <Button fullWidth variant="contained" size="large" type="submit" disabled={loading}
              sx={{ height: 52, fontSize: 17 }}>
              {loading ? 'Entrando...' : 'Entrar'}
            </Button>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1.5, textAlign: 'center' }}>
              Es tu usuario de siempre. Entras una vez y la sesion se mantiene.
            </Typography>
          </form>
        )}

        {mode === 'technician' && (<>
        <TextField
          fullWidth
          label="Codigo de acceso"
          value={loginCode}
          onChange={(e) => setLoginCode(e.target.value.toUpperCase())}
          sx={{ mb: 2 }}
          inputProps={{ style: { fontSize: 22, letterSpacing: 2, textAlign: 'center' } }}
          autoFocus={!loginCode}
        />

        {/* Puntos del PIN */}
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 2 }}>
          {Array.from({ length: PIN_LENGTH }).map((_, i) => (
            <Box key={i} sx={{
              width: 16, height: 16, borderRadius: '50%',
              border: '2px solid', borderColor: 'primary.main',
              backgroundColor: i < pin.length ? 'primary.main' : 'transparent',
            }} />
          ))}
        </Stack>

        {/* Teclado numerico: teclas grandes, usables con guantes */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
          {keypad.map((d) => (
            <Button key={d} variant="outlined" onClick={() => pressDigit(d)}
              disabled={loading}
              sx={{ height: 64, fontSize: 26, fontWeight: 500 }}>
              {d}
            </Button>
          ))}
          <Box />
          <Button variant="outlined" onClick={() => pressDigit('0')} disabled={loading}
            sx={{ height: 64, fontSize: 26, fontWeight: 500 }}>
            0
          </Button>
          <IconButton onClick={() => setPin(pin.slice(0, -1))} disabled={loading || !pin}
            sx={{ height: 64 }} aria-label="Borrar">
            <BackspaceIcon />
          </IconButton>
        </Box>

        <Button
          fullWidth variant="contained" size="large" sx={{ mt: 2, height: 52, fontSize: 17 }}
          disabled={loading || !loginCode.trim() || pin.length < PIN_LENGTH}
          onClick={() => submit(pin)}
        >
          {loading ? 'Entrando...' : 'Entrar'}
        </Button>

        </>)}
      </Paper>
    </Box>
  );
}
